export const businessCategories = [
  'Restaurant / Cafe / Food', 'Retail Store / Boutique', 'Salon / Barber / Spa',
  'Construction / Contractor', 'Plumbing / HVAC / Electrical',
  'Cleaning / Landscaping / Home Services', 'Real Estate / Property Management',
  'Accounting / Financial Services', 'Legal / Professional Services', 'Healthcare / Wellness',
  'Education / Tutoring', 'Automotive Services', 'Events / Photography',
  'Consulting / Technology', 'Nonprofit / Community Organization', 'Other',
] as const
export const customerGoals = [
  'Business website', 'Business cards / stationery', 'Flyers / brochures / direct mail',
  'Signs / banners', 'SEO / online visibility', 'Digital advertising',
  'Newsletter / email marketing', 'Business automation / technology', 'Not sure yet',
] as const
export const usStates = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY AS GU MP PR VI'.split(' ')
export const colorChoices = ['No preference', 'Red', 'Blue', 'Green', 'Black & white', 'Neutral', 'Other / custom'] as const
export const termsVersion = 'free-starter-2026-11-03'
export const acknowledgment = 'I understand this is an inquiry subject to eligibility review. Eligible domain registration is $15 for the first year; optional hosting after 12 months is $9.99/month or $99/year. Domain renewals are separate. This does not authorize recurring charges.'

export interface WebsiteApplication {
  name: string; email: string; phone: string; businessName: string
  category: string; categoryOther: string; city: string; state: string; zip: string
  goals: string[]; specifics: string; website: string
  domainOption: 'new' | 'existing' | 'help'; domain: string
  color: string; customColor: string; description: string
  terms: boolean; marketingOptIn: boolean
}
export type FieldErrors = Partial<Record<keyof WebsiteApplication, string>>
export const initialApplication: WebsiteApplication = {
  name: '', email: '', phone: '', businessName: '', category: '', categoryOther: '',
  city: '', state: 'TX', zip: '', goals: ['Business website'], specifics: '', website: '',
  domainOption: 'help', domain: '', color: 'No preference', customColor: '', description: '',
  terms: false, marketingOptIn: false,
}
export const aboutFields: (keyof WebsiteApplication)[] = ['name', 'email', 'phone', 'businessName', 'category', 'categoryOther', 'city', 'state', 'zip']

export function normalizePhone(value: string): string | null {
  if (!/^[+\d\s().-]+$/.test(value)) return null
  let digits = value.replace(/\D/g, '')
  if (value.startsWith('00')) digits = digits.slice(2)
  if (digits.length === 10 && !value.startsWith('+') && !value.startsWith('00')) digits = `1${digits}`
  if (digits.startsWith('1')) return /^1[2-9]\d{2}[2-9]\d{6}$/.test(digits) ? `+${digits}` : null
  return (value.startsWith('+') || value.startsWith('00')) && /^[2-9]\d{7,14}$/.test(digits) ? `+${digits}` : null
}
export function formatPhone(value: string) {
  const phone = normalizePhone(value.trim())
  return phone?.startsWith('+1') ? `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}` : phone ?? value
}
export function normalizeWebsite(value: string, domainOnly = false): string | null {
  if (/\s/.test(value)) return null
  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`)
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.port) return null
    if (!/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(url.hostname) || url.hostname.length > 253) return null
    if (domainOnly && (url.pathname !== '/' || url.search || url.hash)) return null
    return domainOnly ? url.hostname.toLowerCase() : url.href
  } catch { return null }
}

// Shared allowlist validation: no dictionary/gibberish tests, HTML interpretation or inferred approval.
export function validateApplication(input: unknown, step: 'about' | 'all' = 'all') {
  const raw = input && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {}
  const data: WebsiteApplication = { ...initialApplication, goals: [] }
  const errors: FieldErrors = {}
  const lengths = { name: 100, email: 254, phone: 40, businessName: 150, category: 100, categoryOther: 100, city: 100, state: 2, zip: 10, specifics: 500, website: 250, domain: 253, color: 40, customColor: 100, description: 500 } as const
  for (const [field, max] of Object.entries(lengths)) {
    const key = field as keyof typeof lengths
    const value = raw[key]
    const text = typeof value === 'string' ? value.normalize('NFC').trim() : ''
    data[key] = text
    if (value !== undefined && typeof value !== 'string') errors[key] = 'Enter text in this field.'
    else if (text.length > max) errors[key] = `Use ${max} characters or fewer.`
    else if (Array.from(text).some(c => (c.charCodeAt(0) < 32 && !(key === 'description' && ['\n', '\r', '\t'].includes(c))) || c.charCodeAt(0) === 127)) errors[key] = 'Remove unsupported control characters.'
  }
  for (const key of ['name', 'email', 'phone', 'businessName', 'category', 'city', 'state', 'zip'] as const) {
    if (!data[key]) errors[key] = 'This field is required.'
  }
  if (data.name && !/\p{L}/u.test(data.name)) errors.name = 'Enter your name, including at least one letter.'
  if (data.businessName && !/[\p{L}\p{N}]/u.test(data.businessName)) errors.businessName = 'Enter your business name.'
  data.email = data.email.toLowerCase()
  if (data.email && (!/^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i.test(data.email) || data.email.split('@')[0].length > 64 || /(^\.|\.\.|\.@)/.test(data.email))) errors.email = 'Enter a valid email, such as you@example.com.'
  if (data.phone) {
    const phone = normalizePhone(data.phone)
    if (!phone) errors.phone = 'Enter a 10-digit US phone number, or an international number beginning with + and its country code.'
    else data.phone = phone
  }
  if (!(businessCategories as readonly string[]).includes(data.category)) errors.category = 'Select a business category from the list.'
  if (data.category === 'Other' && !data.categoryOther) errors.categoryOther = 'Describe your business category.'
  if (data.category !== 'Other') data.categoryOther = ''
  if (!usStates.includes(data.state)) errors.state = 'Select a US state or territory.'
  if (!/^\d{5}(?:-\d{4})?$/.test(data.zip)) errors.zip = 'Enter a ZIP code, such as 75165 or 75165-1234.'
  if (!['new', 'existing', 'help'].includes(String(raw.domainOption))) errors.domain = 'Select a domain option.'
  else data.domainOption = raw.domainOption as WebsiteApplication['domainOption']
  if (data.domainOption !== 'help') {
    const domain = normalizeWebsite(data.domain, true)
    if (!domain) errors.domain = 'Enter a domain such as example.com (without a page path).'
    else data.domain = domain
  } else data.domain = ''
  if (data.website) {
    const website = normalizeWebsite(data.website)
    if (!website) errors.website = 'Enter a valid website URL or domain, such as example.com.'
    else data.website = website
  }
  if (!Array.isArray(raw.goals) || raw.goals.length < 1 || raw.goals.length > customerGoals.length || raw.goals.some(g => typeof g !== 'string' || !(customerGoals as readonly string[]).includes(g))) errors.goals = 'Choose at least one goal, or “Not sure yet”.'
  else data.goals = customerGoals.filter(g => (raw.goals as string[]).includes(g))
  if (!(colorChoices as readonly string[]).includes(data.color)) errors.color = 'Select a color preference.'
  if (data.color !== 'Other / custom') data.customColor = ''
  data.terms = raw.terms === true
  data.marketingOptIn = raw.marketingOptIn === true
  if (!data.terms) errors.terms = 'Please acknowledge the offer terms to continue.'
  if (raw.marketingOptIn !== undefined && typeof raw.marketingOptIn !== 'boolean') errors.marketingOptIn = 'Choose whether to receive marketing offers.'
  if (step === 'about') for (const key of Object.keys(errors) as (keyof WebsiteApplication)[]) if (!aboutFields.includes(key)) delete errors[key]
  return { data, errors, valid: Object.keys(errors).length === 0 }
}
